<template>
  <div>
    <HighChart :options="chartOptions" />
  </div>
</template>

<script>

import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'
// import moment from 'moment'
import rca from 'rainbow-colors-array'
import RailObjectsApi from '../../services/api/RailObjectsApi'

Highcharts.SVGRenderer.prototype.symbols.cross = function (x, y, w, h) {
  return ['M', x + 0.5 * w, y, 'L', x + 0.5 * w, y + h, 'z']
}
if (Highcharts.VMLRenderer) {
  Highcharts.VMLRenderer.prototype.symbols.cross = Highcharts.SVGRenderer.prototype.symbols.cross
}

export default {
  name: 'SimpleChart',
  components: {
    HighChart: Chart
  },
  props: {
    sensors: Array,
    measurements: {
      required: false,
      default: () => []
    },
    measurementIds: {
      required: false,
      default: () => []
    },
    offsets: {
      required: true,
      default: () => {}
    },
    title: {
      required: false
    },
    yAxisTitle: String,
    activeMeasurement: {
      required: false
    },
    referenceMeasurement: {
      required: false
    },
    chartMinX: {
      required: false,
      default: 0
    },
    chartMaxX: {
      required: false,
      default: 0
    },
    chartMinY: {
      required: false,
      default: 0
    },
    chartMaxY: {
      required: false,
      default: 0
    },
    measurementHover: {
      required: false
    },
    event: {
      required: false
    },
    activeSignal: {
      required: false
    },
    id: {
      required: true
    }
  },
  data () {
    return {
      dashStyles: [
        'Solid',
        'ShortDash',
        'ShortDot',
        'ShortDashDot',
        'ShortDashDotDot',
        'Dot',
        'Dash',
        'LongDash',
        'DashDot',
        'LongDashDot',
        'LongDashDotDot'
      ],
      activeSensors: [],
      chartOptions: this.getChartOptions(this),
      dict: {},
      tmp: null
    }
  },
  computed: {
    colorMap () {
      let result = {}
      let index = 0
      if (this.measurementIds.filter(m => m.children.length > 0).length > 0) {
        const colors = rca(Math.max(8, this.measurementIds.filter(m => m.children.length > 0).length), 'hex').map(x => '#' + x.hex) // pre-load a rca array of at least 8
        this.measurementIds.filter(m => m.children.length > 0).forEach(m => {
          m.children.forEach(c => {
            result[c.id] = colors[index]
          })
          index++
        })
      } else {
        // need an else branch incase we do not have children, we're the child.
        const colors = rca(Math.max(8, this.measurementIds.length), 'hex').map(x => '#' + x.hex) // pre-load a rca array of at least 8
        for (index = 0; index < this.measurementIds.length; index++) {
          result[this.measurementIds[index].id] = colors[index]
        }
      }
      return result
    },
    uniqueMeasurementIds () {
      let result = [...new Set(this.measurements.map(s => s.MeasurementId))]
      result = result.sort((a, b) => b - a)
      return result
    } // list of unique measurementIds
  },
  mounted: function () {
    this.setActiveSensors()
    this.redraw()
  },
  methods: {
    setActiveSensors: function () {
      if (this.activeMeasurement) {
        this.activeSensors.length = 0
        var measurementSensors = this.sensors.filter(s => s.RailObjectId === this.activeMeasurement.RailObjectId)

        if (measurementSensors.length === 0) {
          measurementSensors = this.sensors
        }

        measurementSensors.forEach(s => {
        // als unit of measure nog niet in lijst zit toevoegen
          if (this.activeSensors.length > 0) {
            if (!this.activeSensors.some(se => se.UnitOfMeasure === s.ConfigSensor.UnitOfMeasure)) {
              this.activeSensors.push(s.ConfigSensor)
            }

            this.activeSensors.forEach(avs => {
              if (avs.UnitOfMeasure === s.ConfigSensor.UnitOfMeasure) {
                if (avs.RangeFrom <= 0 && s.ConfigSensor.RangeFrom <= 0) {
                // Negatief dus laagste pakken
                  avs.RangeFrom = Math.min(avs.RangeFrom, s.ConfigSensor.RangeFrom)
                }

                if (avs.RangeFrom > 0 && s.ConfigSensor.RangeFrom > 0) {
                // Positief dan hoogste pakken
                  avs.RangeFrom = Math.max(avs.RangeFrom, s.ConfigSensor.RangeFrom)
                }

                if (avs.RangeTo <= 0 && s.ConfigSensor.RangeTo <= 0) {
                // Negatief dus laagste pakken
                  avs.RangeTo = Math.min(avs.RangeTo, s.ConfigSensor.RangeTo)
                }

                if (avs.RangeTo > 0 && s.ConfigSensor.RangeTo > 0) {
                // Positief dan hoogste pakken
                  avs.RangeTo = Math.max(avs.RangeTo, s.ConfigSensor.RangeTo)
                }
              }
            })
          } else {
            this.activeSensors.push(s.ConfigSensor)
          }
        })
      }
    },
    getChildName: async function (id) {
      if (this.dict.hasOwnProperty(id)) {
        return this.dict[id]
      } else {
        await this.getChildNameViaApi(id)
        this.dict[id] = this.tmp.Name
        this.tmp = null
        return this.dict[id]
      }
    },
    getChildNameViaApi: async function (id) {
      await RailObjectsApi.getRailObjectById(id).then(response => { this.tmp = response })
    },
    getChartOptions (t) {
      return {
        chart: {
          height: 300,
          zoomType: 'xy',
          animation: false,
          marginLeft: 70,
          marginRight: 70,
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        exporting: {
          enabled: true
        },
        title: {
          align: 'left',
          x: 60,
          text: ''
        },
        accessibility: {
          enabled: false
        },
        lang: {
          noData: this.$t('views.switch.nodata'),
          viewFullscreen: this.$t('charts.exporting.fullscreen'),
          printChart: this.$t('charts.exporting.printchart'),
          downloadPNG: this.$t('charts.exporting.downloadpng'),
          downloadJPEG: this.$t('charts.exporting.downloadjpeg'),
          downloadPDF: this.$t('charts.exporting.downloadpdf'),
          downloadSVG: this.$t('charts.exporting.downloadsvg'),
          downloadCSV: this.$t('charts.exporting.downloadcsv'),
          downloadXLS: this.$t('charts.exporting.downloadxls'),
          viewData: this.$t('charts.exporting.viewdatatable'),
          hideData: this.$t('charts.exporting.hidedatatable')
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: ''
        },
        legend: {
          enabled: true
        },
        plotOptions: {
          series: {
            events: {
              legendItemClick () {
                const id = this.userOptions.measurementId
                if (id === -1) {
                  return true
                } else {
                  t.setActiveMeasurement(id)
                  return false
                }
              }
            }
          }
        },
        xAxis: {
          title: {
            text: this.$t('views.switch.time') + ' (s)'
          },
          min: 0,
          max: 0,
          tickInterval: 0.2,
          plotBands: [],
          plotLines: []
        },
        yAxis: [
          {
            min: this.chartMinY,
            max: this.chartMaxY,
            lineWidth: 1,
            softMin: 0,
            title: {
              text: (this.yAxisTitle === null ? this.$t('views.switch.powerunitfull') : this.yAxisTitle),
              offset: 50
            },
            opposite: false,
            plotLines: []
          }
        ],
        series: [
          {
            name: 'date',
            type: 'line',
            data: [],
            turboThreshold: 10000
          }
        ],
        tooltip: {
          formatter: function () {
            return this.points.reduce(function (s, point) {
              return `${s}<br/><span style="color: ${point.color}">${point.series.userOptions.objectName}</span>: ${parseFloat(point.y).toFixed(5)} ${t.chartOptions.yAxis[point.series.userOptions.yAxis].title.text}`
            }, '<b>Time:' + this.x + ' s</b>')
          },
          shared: true,
          crosshairs: true
        }
      }
    },
    getActive (measurement) {
      let result
      if (this.measurementHover !== null && measurement) {
        result = measurement.MeasurementId === this.measurementHover.MeasurementId
      } else if (measurement && this.activeMeasurement) {
        result = measurement.MeasurementId === this.activeMeasurement.MeasurementId
      }
      return result
    },
    setYAxisTitle: function (unitOfMeasure, index) {
      this.chartOptions.yAxis[0].title.text = unitOfMeasure
    },
    clearYAxisTitles: function () {
      for (let i = 0; i < this.chartOptions.yAxis.length; i++) {
        this.chartOptions.yAxis[i].title.text = ''
      }
    },
    getSensorTypeForRailObject: function (RailObjectId, SensorType) {
      var retval = false
      this.sensors.forEach(s => {
        if (s.RailObjectId === RailObjectId && s.ConfigSensor.UnitOfMeasure === SensorType) {
          retval = true
        }
      })
      return retval
    },
    redraw: function () {
      let series = []
      let color = ''
      if (this.measurements) {
        this.measurements.forEach(m => {
          let length = m.MeasurementPoints.length
          if (length === 0) {
            // no points, do not draw
          } else {
            const active = this.getActive(m)
            if (active) {
              color = this.colorMap[m.MeasurementId]
            }
            const severityIcon = ['', '', '⚠️', '🔺'][m.SeverityId]
            const dateTime = Highcharts.dateFormat('%e %b %Y %H:%M:%S', this.$options.filters.correctedMomentDate(m.MeasurementDateTime).toDate())
            const tempPerc = m.Temperature !== null ? `[${m.Temperature}℃${this.formatPerc(m.PowerDiffPerc)}]` : `[--℃]`

            var yAxisIndex = 0
            var dashStyle = 'solid'
            series.push({
              name: `${severityIcon} ${dateTime} ${this.directionString(m.Direction)} ${tempPerc}`,
              objectName: m.ObjectName || '',
              measurementId: m.MeasurementId,
              type: 'line',
              lineWidth: active ? 4 : 2,
              dashStyle: dashStyle,
              color: this.colorMap[m.MeasurementId],
              marker: {
                enabled: false
              },
              zIndex: active ? 2 : 0,
              legendIndex: active ? 0 : 2,
              stickyTracking: false,
              data: m.MeasurementPoints.map(p => {
                return {
                  x: parseFloat(((this.offsets[m.RailObjectId][m.MeasurementId] * 0.001) + p.X).toFixed(5)),
                  y: p.Y,
                  marker: null
                }
              }),
              turboThreshold: 10000,
              yAxis: yAxisIndex,
              visible: true
            })
          }

          if (series.length === 0) {
            return
          }

          if (!series[series.length - 1].data[m.MeasurementPoints.length - 1]) {
            return
          }

          series[series.length - 1].data[m.MeasurementPoints.length - 1].marker = {
            enabled: true,
            symbol: 'cross',
            lineColor: 'black',
            lineWidth: 2
          }
        })
      }
      if (this.referenceMeasurement) {
        series.push({
          name: this.$t('views.switch.chart.reference') + ': ' +
            this.$options.filters.correctedMomentDate(this.referenceMeasurement.MeasurementDateTime).format('DD-MM-YY kk:mm:ss') + '[' +
            this.referenceMeasurement.Temperature + '℃]',
          measurementId: -1,
          objectName: '',
          type: 'line',
          lineWidth: 1,
          color: color,
          dashStyle: 'Dash',
          marker: {
            enabled: false
          },
          zIndex: 1,
          legendIndex: 1,
          stickyTracking: false,
          data: this.referenceMeasurement.MeasurementPoints.map(p => {
            return {
              x: p.X.toFixed(5),
              y: p.Y
            }
          }),
          turboThreshold: 10000
        })
      }
      this.chartOptions.series = series
      this.calculateMaximum()
    },
    async calculateMaximum () {
      let max = 0
      await this.chartOptions.series.forEach((s) => {
        let last = s.data[s.data.length - 1].x
        if (last > max) {
          max = last
        }
      })
      max = parseFloat(max)
      this.$emit('set-max', max)
    },
    formatPerc (perc) {
      return perc !== null ? `, ${perc}%` : ''
    },
    setActiveMeasurement (itemId) {
      const item = this.measurements.find(m => {
        return m.MeasurementId === itemId
      })
      return this.$emit('on-select-measurement', item)
    },
    directionString (dir) {
      const directions = ['N', 'R']
      return directions[dir]
    }
  },
  watch: {
    measurements (newVal, oldVal) {
      this.setActiveSensors()
      if (oldVal.length !== newVal.length) {
        this.redraw()
      }
    },
    chartMinX () {
      this.chartOptions.xAxis.min = this.chartMinX
      this.redraw()
    },
    chartMaxX: {
      immediate: true,
      handler () {
        let oldMax = this.chartOptions.xAxis.max
        this.chartOptions.xAxis.max = this.chartMaxX
        if (oldMax !== this.chartMaxX) {
          this.redraw()
        }
      }
    },
    chartMinY () {
      this.chartOptions.yAxis[0].min = this.chartMinY
      this.redraw()
    },
    chartMaxY () {
      this.chartOptions.yAxis[0].max = this.chartMaxY
      this.redraw()
    },
    title: {
      immediate: true,
      handler () {
        this.chartOptions.title.text = this.title
      }
    },
    activeMeasurement () {
      this.redraw()
    },
    referenceMeasurement () {
      this.redraw()
    },
    measurementHover (val) {
      this.redraw()
    },
    event (val) {
      if (!val) {
        this.chartOptions.xAxis.plotBands = []
        this.chartOptions.yAxis[0].plotLines = []
      } else {
        this.chartOptions.xAxis.plotBands = [{
          color: val.SeverityId === 2 ? '#FFA' : '#FAA',
          from: val.SectionStart,
          to: val.SectionEnd,
          zIndex: 1,
          label: {
            text: val.SectionName
          }
        }]
        if (!val.AnalyseThresholds.AnalyseMethodType.includes('Margin')) {
          this.chartOptions.yAxis[0].plotLines = [
            {
              color: '#FF0',
              width: 2,
              zIndex: 10,
              value: val.AnalyseThresholds.Warning.Value,
              label: {
                text: 'Warning: ' + val.AnalyseThresholds.Warning.Value
              }
            },
            {
              color: '#F00',
              width: 2,
              zIndex: 10,
              value: val.AnalyseThresholds.Error.Value,
              label: {
                text: 'Error: ' + val.AnalyseThresholds.Error.Value
              }
            }
          ]
        } else {
          this.chartOptions.yAxis[0].plotLines = []
        }
      }
    },
    activeSignal (val) {
      if (!val) {
        this.chartOptions.xAxis.plotLines = []
      } else {
        this.chartOptions.xAxis.plotLines = [
          {
            color: '#66F',
            width: 2,
            zIndex: 5,
            value: val.low
          },
          {
            color: '#66F',
            width: 2,
            zIndex: 5,
            value: val.high
          }
        ]
      }
    },
    yAxisTitle: {
      immediate: true,
      handler (newVal) {
        this.setYAxisTitle(newVal, 1)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/switch/SimpleChart.vue